import {castArray, every, find, flatMap, forEach, last, map, mapValues, merge, size, values} from 'lodash';

import CustomTelemetryExpressionParser from './CustomTelemetryExpressionParser';
import {BUILTIN_FUNCTIONS, BUILTIN_STRING_METHODS} from './ace-mode-custom-telemetry-expression';

export const INDENT = Symbol('INDENT');
export const NEWLINE = Symbol('NEWLINE');
export const OUTDENT = Symbol('OUTDENT');

export function interpose(array, separator) {
  return flatMap(array, (item, index) => index ? castArray(separator).concat(item) : item);
}

let visitorInstance = null;

const CustomTelemetryExpressionParserCSTVisitor =
  CustomTelemetryExpressionParser.instance.getBaseCstVisitorConstructor();

export default class CustomTelemetryExpressionTypeResolver extends CustomTelemetryExpressionParserCSTVisitor {
  static get instance() {
    if (!visitorInstance) visitorInstance = new this();
    return visitorInstance;
  }

  static resetState() {
    const {instance} = this;
    instance.functionNames = [];
    instance.chainStartedForStackSize = null;
    instance.forbidNewLines = false;
    instance.currentValueTypes = [];
  }

  static resolveType(cst, options) {
    try {
      return CustomTelemetryExpressionTypeResolver.run(cst, options);
    } catch {}
    return null;
  }

  static run(cst, {knownVariables = {}} = {}) {
    this.resetState();
    this.instance.knownVariables = mapValues(knownVariables, 'propertySchema');
    return this.instance.visit(cst);
  }

  getKnownVariableType(variableName) {
    return this.knownVariables[variableName] ?? null;
  }

  expr(ctx) {
    let result = this.visit(ctx.primaryExpr);
    forEach(ctx.primaryExpressionOperation, (operation) => {
      this.currentValueTypes.push(result);
      result = this.visit(operation);
      this.currentValueTypes.pop();
    });

    return result;
  }

  primaryExpr(ctx) {
    return (
      ctx.identifier ? this.visit(ctx.identifier) :
      ctx.literal ? this.visit(ctx.literal) :
      ctx.listExpr ? this.visit(ctx.listExpr) :
      ctx.dictionaryExpr ? this.visit(ctx.dictionaryExpr) :
      ctx.callExpr ? this.visit(ctx.callExpr) :
      ctx.bracketsExpr ? this.visit(ctx.bracketsExpr) :
      null
    );
  }

  primaryExpressionOperation(ctx) {
    return (
      ctx.attribute ? this.visit(ctx.attribute) :
      ctx.indexingOrSliceExpr ? this.visit(ctx.indexingOrSliceExpr) :
      ctx.methodExpr ? this.visit(ctx.methodExpr) :
      ctx.conditionalExpression ? this.visit(ctx.conditionalExpression) :
      null
    );
  }

  attribute(ctx) {
    const currentValueType = last(this.currentValueTypes);
    const field = ctx.identifier[0].image;
    return currentValueType?.properties?.[field] ?? null;
  }

  indexingOrSliceExpr(ctx) {
    const currentValueType = last(this.currentValueTypes);
    if (ctx.Colon) { // slice
      return currentValueType;
    }
    // indexing
    const indexType = this.visit(ctx.fromIndex[0]);
    if (currentValueType.type === 'array') {
      return currentValueType.items ?? null;
    } else if (indexType && 'const' in indexType) {
      return currentValueType?.properties?.[indexType.const] ?? null;
    } else if (size(currentValueType?.properties) > 0) {
      const firstProperty = Object.values(currentValueType?.properties)[0];
      if (firstProperty.type !== 'object' &&
          every(currentValueType.properties, {type: firstProperty.type})) {
        return {type: firstProperty.type};
      }
    }
    return currentValueType;
  }

  methodExpr(ctx) {
    const currentFunctionName = ctx.functionName[0]?.children?.Identifier[0]?.image;
    const stringMethod = find(BUILTIN_STRING_METHODS, {name: currentFunctionName});
    const commonMethod = find(BUILTIN_FUNCTIONS, {name: currentFunctionName});
    return stringMethod?.returnType ||
      commonMethod?.returnType ||
      null;
  }

  conditionalExpression(ctx) {
    const currentValueType = last(this.currentValueTypes);
    const falseExpressionType = this.visit(ctx.falseExpression[0]);
    if (currentValueType?.type && currentValueType.type === falseExpressionType.type) {
      return {type: currentValueType.type};
    }
    return null;
  }

  bracketsExpr(ctx) {
    return this.visit(ctx.expr[0]);
  }

  callExpr(ctx) {
    const currentFunctionName = ctx.functionName[0].image;
    const stringMethod = find(BUILTIN_STRING_METHODS, {name: currentFunctionName});
    const commonMethod = find(BUILTIN_FUNCTIONS, {name: currentFunctionName});
    return stringMethod?.returnType ||
      commonMethod?.returnType ||
      null;
  }

  identifier(ctx) {
    return this.getKnownVariableType(ctx.Identifier[0].image);
  }

  literal(ctx) {
    return (
      ctx.stringLiteral ? this.visit(ctx.stringLiteral) :
      ctx.booleanLiteral ? this.visit(ctx.booleanLiteral) :
      ctx.hexIntegerLiteral ? this.visit(ctx.hexIntegerLiteral) :
      ctx.floatLiteral ? this.visit(ctx.floatLiteral) :
      ctx.decimalIntegerLiteral ? this.visit(ctx.decimalIntegerLiteral) :
      ctx.noneLiteral ? this.visit(ctx.noneLiteral) :
      null
    );
  }

  stringLiteral(ctx) {
    const image = values(ctx)[0][0]?.image;
    return {type: 'string', const: image && image.slice(1, -1)};
  }

  booleanLiteral(ctx) {
    const image = values(ctx)[0][0]?.image;
    return {type: 'boolean', const: image === 'True'};
  }

  hexIntegerLiteral(ctx) {
    const image = values(ctx)[0][0]?.image;
    return {type: 'number', const: image && parseInt(image)};
  }

  floatLiteral(ctx) {
    const image = values(ctx)[0][0]?.image;
    return {type: 'number', const: image && parseFloat(image)};
  }

  decimalIntegerLiteral(ctx) {
    const image = values(ctx)[0][0]?.image;
    return {type: 'number', const: image && parseInt(image)};
  }

  noneLiteral() {
    return {type: 'null'};
  }

  listExpr(ctx) {
    const itemTypes = map(ctx.listItem, (item) => this.visit(item));
    if (itemTypes.length > 0 && every(itemTypes, {type: itemTypes[0].type})) {
      return {type: 'array', items: {type: itemTypes[0].type}};
    }
    return {type: 'array'};
  }

  listItem(ctx) {
    return this.visit(ctx.expr);
  }

  dictionaryExpr(ctx) {
    const result = {type: 'object'};
    forEach(ctx.keyValuePair, (pair) => {
      result.properties = merge(
        result.properties,
        this.visit(pair)
      );
    });
    return result;
  }

  keyValuePair(ctx) {
    const keyType = this.visit(ctx.key[0]);
    if (keyType && 'const' in keyType) {
      const valueType = this.visit(ctx.value[0]);
      return {[keyType.const]: valueType};
    }
    return null;
  }
}
